// src/components/ui/shop/itemGrid.js
// eslint-disable-next-line no-unused-vars
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { processTooltip } from '../../../utils/tooltipHelper';

const ItemGrid = ({ items, currencyMode, handleBuy, currentBalance }) => {
  const { t } = useTranslation();

  return (
    <GridContainer>
      {items.map(item => {
        // 1. Preisermittlung
        let basePrice;
        if (item.shop_type === 1) {
          basePrice = item.arcanium_price;
        } else if (item.shop_type === 2) {
          basePrice = item.vote_price;
        } else {
          basePrice = currencyMode === 'arcanium' ? item.arcanium_price : item.vote_price;
        }

        // 2. Rabattberechnung
        let discountedPrice = null;
        if (item.event_active && Number(item.discount) > 0) {
          discountedPrice = Math.round(basePrice - (basePrice * item.discount / 100));
        }
        const finalPrice = discountedPrice ?? basePrice;
        const isDisabled = finalPrice > currentBalance;

        // 3. Tooltip-Text erzeugen
        const tooltipText = processTooltip(item);
        return (
          <ItemCard key={item.product_id}>
            <IconWrapper>
              <ItemIcon
                src={item.icon}
                alt={item.item_name}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/assets/items/unknown.jpg';
                }}
              />
              <Tooltip dangerouslySetInnerHTML={{ __html: tooltipText }} />
            </IconWrapper>
            <ItemInfo>
              <ItemName>
                {item.item_count}x {item.item_name}
              </ItemName>
            </ItemInfo>
            <ItemPrice>
              {discountedPrice ? (
                <>
                  <OriginalPrice>{basePrice}</OriginalPrice> {discountedPrice}{' '}
                  {currencyMode === 'arcanium' ? t('itemGrid.currency.arc') : t('itemGrid.currency.vp')}
                </>
              ) : (
                <>
                  {basePrice} {currencyMode === 'arcanium' ? t('itemGrid.currency.arc') : t('itemGrid.currency.vp')}
                </>
              )}
            </ItemPrice>
            <BuyButton onClick={() => handleBuy(item)} disabled={isDisabled}>
              {t('itemGrid.buyButton')}
            </BuyButton>
          </ItemCard>
        );
      })}
    </GridContainer>
  );
};

export default ItemGrid;

// ============ Styled Components ============

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px;
  margin-bottom: 10px;

  @media (max-width: 1199px) {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
`;

const ItemCard = styled.div`
  background-image: url(/assets/ui/container-big-w-bg.png);
  background-repeat: no-repeat;
  background-position: center center;
  background-size: 100% 100%;
  padding: 10px;
  text-align: center;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`;

const IconWrapper = styled.div`
  position: relative;
  &:hover > div.tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const ItemIcon = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  margin-bottom: 10px;
`;

const Tooltip = styled.div.attrs(() => ({ className: 'tooltip' }))`
  position: absolute;
  bottom: 110%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0,0,0,0.85);
  color: #fff;
  padding: 6px 10px;
  border-radius: 4px;
  white-space: normal;  /* Damit Zeilen umbrechen */
  font-size: 0.85rem;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease;
  z-index: 10;
  width: 300px;
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ItemName = styled.div`
  font-weight: bold;
  margin: 4px 0;
  height: 48px;

  @media (max-width: 768px) {
    font-size: 12px;
    height: 68px;
  }

`;

const ItemPrice = styled.div`
  margin-bottom: 10px;
  font-size: 1.1rem;
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const OriginalPrice = styled.span`
  text-decoration: line-through;
  margin-right: 5px;
  color: #888;
`;

const BuyButton = styled.button`
  border: none;
  color: white;
  background-color: #28a745;
  padding: 6px 10px;
  cursor: pointer;
  border-radius: 4px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    max-width: 100px;
    max-height: 50px;
  }
`;
